import type { AppListenerEffectAPI, AppStartListening } from '@/redux/store';
import { PluginsEventBus } from '@/services/pluginsManager/pluginsEventBus';
import { getProjectById } from '@/redux/project/project.thunks';
import { Action, createListenerMiddleware } from '@reduxjs/toolkit';
import { mapDataSelector } from '../map.selectors';
import {
  initMapBackground,
  initMapPosition,
  initMapSizeAndModelId,
  initOpenedMaps,
} from '../map.thunks';

export const pluginEventsListenerMiddleware = createListenerMiddleware();

const startListening = pluginEventsListenerMiddleware.startListening as AppStartListening;

/**
 * Actions that bootstrap the map. The init thunks dispatch their own plugin events (see
 * map.thunks.ts), so the state change they cause must not be reported a second time here.
 */
const BOOTSTRAP_ACTIONS: string[] = [
  initMapPosition.fulfilled.type,
  initMapSizeAndModelId.fulfilled.type,
  initMapBackground.fulfilled.type,
  initOpenedMaps.fulfilled.type,
  getProjectById.fulfilled.type,
];

/**
 * Plugin callbacks are run synchronously by the event bus, so they must never be called from a
 * reducer - a callback reading the store while the reducer runs throws redux error #3, and one
 * dispatching an action throws redux error #2. This listener runs after the state update is
 * finished and derives the events from the state change itself, which also makes them
 * independent of which action caused the change.
 */
export const pluginEventsMiddlewareListener = async (
  action: Action,
  { getOriginalState, getState }: AppListenerEffectAPI,
): Promise<void> => {
  if (BOOTSTRAP_ACTIONS.includes(action.type)) {
    return;
  }

  const previous = mapDataSelector(getOriginalState());
  const current = mapDataSelector(getState());
  const { modelId } = current;

  if (previous.modelId !== modelId) {
    PluginsEventBus.dispatchEvent('onSubmapClose', previous.modelId);
    PluginsEventBus.dispatchEvent('onSubmapOpen', modelId);
  }

  const previousPosition = previous.position.last;
  const position = current.position.last;

  if (previousPosition.x !== position.x || previousPosition.y !== position.y) {
    PluginsEventBus.dispatchEvent('onCenterChanged', {
      modelId,
      x: position.x,
      y: position.y,
    });
  }

  // a falsy zoom is not reported - the map keeps 0 as the zoom of a map that has been opened but
  // not positioned yet, and plugins were never told about that pseudo-zoom
  if (position.z && previousPosition.z !== position.z) {
    PluginsEventBus.dispatchEvent('onZoomChanged', {
      modelId,
      zoom: position.z,
    });
  }

  if (previous.backgroundId !== current.backgroundId) {
    PluginsEventBus.dispatchEvent('onBackgroundOverlayChange', current.backgroundId);
  }
};

startListening({
  predicate: () => true,
  effect: pluginEventsMiddlewareListener,
});
